import { ChangeDetectionStrategy, Component, effect, input, model, OnDestroy, output, signal, untracked, ViewChild, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { ReportFrameworkComponent } from 'src/app/shared/report-framework/report-framework/report-framework.component';
import { UIIconsComponent } from 'src/app/shared/ui/icons/icons.component';
import { FormsModule } from '@angular/forms';
import { FreeFormTextEditorComponent } from '../free-form-text-editor/free-form-text-editor.component';
import { AssetsApiService } from 'src/app/core/http/services/assets.service';
import { DataCopilotApiService } from 'src/app/core/http/services/data-copilot.service';
import { DataVisualizationComponent } from 'src/app/shared/common-ui-components/data-visualization/data-visualization.component';
import { ChartModel, ColumnState } from 'ag-grid-community';
import { Subscription, of, catchError, map, finalize } from 'rxjs';
import { CardChartConfigurations, CardGridConfigurations } from '@interface/card.interface';
@Component({
  selector: 'app-card',
  standalone: true,
  imports: [
    NgClass,
    ReportFrameworkComponent,
    UIIconsComponent,
    FormsModule,
    FreeFormTextEditorComponent,
    DataVisualizationComponent
  ],
  templateUrl: './card.component.html',
  styleUrl: './card.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardComponent implements OnDestroy {
  @ViewChild(ReportFrameworkComponent) reportFrameworkComp?: ReportFrameworkComponent;
  @ViewChild(FreeFormTextEditorComponent) freeFormEditorComp?: FreeFormTextEditorComponent;
  @ViewChild(DataVisualizationComponent) dataVisualizationComp?: DataVisualizationComponent;

  // Inputs (developer preview)
  id = input<string>('');
  color = input<string>('#ffffff');
  reportId = input<string>('');
  questionId = input<string>('');
  conversationId = input<string>('');
  questionText = input<string>('');
  cardType = input<'REPORT' | 'ANSWER' | 'FREEFORM_CARD'>('REPORT');
  answerId = input<string>('');
  isSelected = input<boolean>(false);
  content = input<string>('');
  assetId = input<string>('');
  isLoadingFreeForm = input<boolean>(false);
  canvasId = input<string>('');
  triggerInsights = input<boolean>(false);
  title = input<string>('');
  cardData = input<any>(null);
  selectedVisualType = input<'TABLE' | 'CHART' | undefined>(undefined);
  chartConfigurations = input<CardChartConfigurations | undefined>(undefined);
  gridConfigurations = input<CardGridConfigurations | undefined>(undefined);

  // Outputs (developer preview)
  deleteCard = output<string>();
  deleteFreeFormCard = output<{ cardId: string, assetId: string }>();
  answerIdUpdate = output<{ cardId: string, answerId: string }>();
  selectionChange = output<{ cardId: string, isSelected: boolean, reportId: string, answerId: string }>();
  freeFormAssetCreated = output<{ cardId: string, assetId: string, content: string }>();
  freeFormSaveStateChange = output<{ cardId: string, hasUnsavedChanges: boolean }>();
  openInsightsModal = output<string>();
  triggerCanvasSave = output<void>();
  insightsGenerated = output<{ reportName: string, insights: string }>();
  insightsError = output<string>();
  titleChanged = output<{ cardId: string, newTitle: string }>();
  cardDataLoaded = output<{ cardId: string, data: any }>();
  visualTypeChanged = output<{ cardId: string, visualType: 'TABLE' | 'CHART' }>();
  chartConfigurationsChanged = output<{ cardId: string, chartConfigurations: CardChartConfigurations }>();
  gridConfigurationsChanged = output<{ cardId: string, gridConfigurations: CardGridConfigurations }>();

  // Local mutable state as signals
  cardTitle = signal<string>('Loading...');
  reportDescription = signal<string>('');
  isLoading = signal<boolean>(true);
  isCanvas = signal<boolean>(true);
  hasError = signal<boolean>(false);
  errorMessage = signal<string>('');
  visualType = signal<'TABLE' | 'CHART'>('TABLE');
  freeFormAssetId = signal<string>('');
  localAssetId = signal<string>('');
  hasUnsavedFreeFormChanges = signal<boolean>(false);
  isEditingTitle = signal<boolean>(false);
  editingTitle = signal<string>('');
  viewMode = signal<'grid' | 'chart'>('grid');
  chartType = signal<string>('bar');
  selectedChart = signal<any>({});
  axisAttributesAndMeasures = signal<any>({});
  answerData = signal<any[]>([]);
  tableData = signal<any>(null);
  localChartModel = signal<ChartModel | null>(null);
  localFilterModel = signal<any>(null);
  localSortModel = signal<ColumnState[]>([]);

  private _latestReportChartConfig: CardChartConfigurations | null = null;
  private _latestReportGridConfig: CardGridConfigurations | null = null;
  private subscription: Subscription = new Subscription();
  private currentAnswerAsset: any = null;
  private localAnswerId = signal<string>('');

  private assetsApiService = inject(AssetsApiService);
  private dataCopilotApiService = inject(DataCopilotApiService);
  private router = inject(Router);

  private _cardDataInitialized = false;

  constructor() {
    // Sync isLoadingFreeForm → isLoading when card is a free-form card
    effect(() => {
      if (this.cardType() === 'FREEFORM_CARD') {
        this.isLoading.set(this.isLoadingFreeForm());
      }
    }, { allowSignalWrites: true });

    // Sync title input → cardTitle for free-form cards
    effect(() => {
      const t = this.title();
      if (t && this.cardType() === 'FREEFORM_CARD') {
        this.cardTitle.set(t);
      }
    }, { allowSignalWrites: true });

    // Trigger insights generation when flag flips to true
    effect(() => {
      if (this.triggerInsights()) {
        untracked(() => this.generateInsights());
      }
    });

    // Load pre-loaded cardData the first time it arrives (replaces ngOnChanges firstChange guard)
    effect(() => {
      const data = this.cardData();
      const type = this.cardType();
      if (data && !this._cardDataInitialized) {
        this._cardDataInitialized = true;
        untracked(() => {
          if (type === 'ANSWER') this.loadPreloadedAnswerData(data);
          else if (type === 'REPORT') this.loadPreloadedReportData(data);
        });
      }
    }, { allowSignalWrites: true });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit() {
    const selVisualType = this.selectedVisualType();
    if (selVisualType) {
      if (this.cardType() === 'REPORT') {
        this.visualType.set(selVisualType);
      } else if (this.cardType() === 'ANSWER') {
        this.viewMode.set(selVisualType === 'TABLE' ? 'grid' : 'chart');
      }
    }

    const chartConf = this.chartConfigurations();
    if (chartConf) {
      if (chartConf.chartModel) {
        this.localChartModel.set(chartConf.chartModel);
      }
      if (chartConf.chartType) {
        this.chartType.set(chartConf.chartType);
      }
      if (this.cardType() === 'REPORT') {
        this._latestReportChartConfig = chartConf;
      }
    }

    const gridConf = this.gridConfigurations();
    if (gridConf) {
      this.localFilterModel.set(gridConf.filterModel || null);
      this.localSortModel.set(gridConf.sortModel || []);
      if (this.cardType() === 'REPORT') {
        this._latestReportGridConfig = gridConf;
      }
    }

    if (this.cardType() === 'ANSWER' && this.questionText()) {
      this.cardTitle.set(this.questionText());
      if (this.cardData()) {
        this._cardDataInitialized = true; // prevent the cardData effect from double-loading
        this.loadPreloadedAnswerData(this.cardData());
      } else {
        this.loadAnswerDataForVisualization();
      }
    } else if (this.cardType() === 'FREEFORM_CARD') {
      this.cardTitle.set(this.title() || 'Free Form Card');
      this.isLoading.set(this.isLoadingFreeForm());
    }

    this.localAssetId.set(this.assetId());
    this.localAnswerId.set(this.answerId());
  }

  onDelete(): void {
    if (this.cardType() === 'FREEFORM_CARD') {
      this.deleteFreeFormCard.emit({ cardId: this.id(), assetId: this.localAssetId() || this.assetId() });
    } else {
      this.deleteCard.emit(this.id());
    }
  }

  onEdit(): void {
    if (this.freeFormEditorComp) {
      this.freeFormEditorComp.startEditing();
    }
  }

  onEditTitleClick(): void {
    if (!this.isSaved) return;
    this.isEditingTitle.set(true);
    this.editingTitle.set(this.cardTitle());
    setTimeout(() => {
      const input = document.querySelector('.title-input') as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 50);
  }

  onTitleSave(): void {
    const trimmedTitle = this.editingTitle().trim();
    if (trimmedTitle) {
      this.cardTitle.set(trimmedTitle);

      this.titleChanged.emit({ cardId: this.id(), newTitle: trimmedTitle });

      const assetId = this.localAssetId() || this.assetId() || this.freeFormAssetId();
      if (assetId) {
        this.updateAssetTitle(trimmedTitle);
      }
      this.triggerCanvasSave.emit();
    }
    this.isEditingTitle.set(false);
    this.editingTitle.set('');
  }

  onTitleCancel(): void {
    this.isEditingTitle.set(false);
    this.editingTitle.set('');
  }

  onTitleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onTitleSave();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.onTitleCancel();
    }
  }

  onSelectionChange(): void {
    this.selectionChange.emit({
      cardId: this.id(),
      isSelected: this.isSelected(),
      reportId: this.reportId(),
      answerId: this.localAnswerId() || this.answerId()
    });
  }

  navigateToReport(): void {
    if (this.cardType() === 'REPORT' && this.reportId()) {
      const baseUrl = window.location.origin;
      const reportUrl = `${baseUrl}/dashboard/reports/saved/single?id=${this.reportId()}`;
      window.open(reportUrl, '_blank');
    }
  }

  updateCardTitle(reportData: { name: string, description: string }): void {
    if (this.cardType() === 'REPORT') {
      this.cardTitle.set(reportData.name);
      this.reportDescription.set(reportData.description || '');
    }
  }

  onDataLoaded(): void {
    this.isLoading.set(false);
    if (this.cardType() === 'REPORT') {
      setTimeout(() => {
        if (this.reportFrameworkComp) {
          const reportData = { reportVisualData: this.reportFrameworkComp.reportVisualData || [] };
          this.cardDataLoaded.emit({ cardId: this.id(), data: reportData });
        }
      }, 100);
    }
  }

  onLoadError(error: string): void {
    this.isLoading.set(false);
    this.hasError.set(true);
    this.errorMessage.set(error || 'Failed to load data');
  }

  onAnswerIdLoaded(answerId: string): void {
    if (this.cardType() === 'ANSWER' && !this.localAnswerId()) {
      this.localAnswerId.set(answerId);
      this.answerIdUpdate.emit({ cardId: this.id(), answerId });

      if (this.isSelected()) {
        this.selectionChange.emit({
          cardId: this.id(),
          isSelected: this.isSelected(),
          reportId: this.reportId(),
          answerId
        });
      }
    }
  }

  onVisualTypeToggle(type: 'TABLE' | 'CHART') {
    this.visualType.set(type);
    if (this.reportFrameworkComp) {
      this.reportFrameworkComp.changeReportVisualType(type);
    }
    this.visualTypeChanged.emit({ cardId: this.id(), visualType: type });
  }

  onDataVisualizationToggle(mode: 'grid' | 'chart'): void {
    this.onViewModeChange(mode);
  }

  onFreeFormAssetCreated(event: { assetId: string, content: string }): void {
    this.freeFormAssetId.set(event.assetId);
    this.localAssetId.set(event.assetId);
    this.freeFormAssetCreated.emit({ cardId: this.id(), assetId: event.assetId, content: event.content });
  }

  onFreeFormSaveStateChange(hasUnsavedChanges: boolean): void {
    this.hasUnsavedFreeFormChanges.set(hasUnsavedChanges);
    this.freeFormSaveStateChange.emit({ cardId: this.id(), hasUnsavedChanges });
  }

  onOpenInsightsModal(): void {
    this.openInsightsModal.emit(this.id());
  }

  onTriggerCanvasSave(): void {
    this.triggerCanvasSave.emit();
  }

  onInsightsGenerated(event: { reportName: string, insights: string }): void {
    this.insightsGenerated.emit(event);
  }

  onInsightsError(error: string): void {
    console.error('Insights generation failed in card:', error);
    this.insightsError.emit(error);
  }

  generateInsights(): void {
    if (this.cardType() === 'REPORT' && this.reportFrameworkComp) {
      this.reportFrameworkComp.generateInsights();
    } else if (this.cardType() === 'ANSWER') {
      this.generateInsightsForAnswer();
    }
  }

  private generateInsightsForAnswer(): void {
    if (this.currentAnswerAsset) {
      this.callInsightsAPI(this.currentAnswerAsset);
      return;
    }

    const currentAnswerId = this.localAnswerId() || this.answerId();
    if (currentAnswerId) {
      this.assetsApiService.getAssetById(currentAnswerId).subscribe({
        next: (response: any) => {
          const answerAsset = response?.data?.asset;
          if (answerAsset) {
            this.currentAnswerAsset = answerAsset;
            this.callInsightsAPI(answerAsset);
          } else {
            this.insightsError.emit('Failed to load answer data for insights generation');
          }
        },
        error: (error: any) => {
          console.error('Error fetching answer asset:', error);
          this.insightsError.emit('Failed to load answer data for insights generation');
        }
      });
      return;
    }

    if (this.questionId()) {
      this.assetsApiService.findAssetsByRelationship([this.questionId()], ['ANSWER'])
        .pipe(
          map((response: any) => response?.data?.findAssetsByRelationship || []),
          catchError(error => {
            console.error('Error getting answer relationships:', error);
            this.insightsError.emit('Failed to load answer data for insights generation');
            return of([]);
          })
        )
        .subscribe({
          next: (answers) => {
            if (!answers?.length) {
              this.insightsError.emit('No answer found for insights generation');
              return;
            }
            const answer = answers[0];
            this.currentAnswerAsset = answer;
            if (answer.id && !this.localAnswerId()) {
              this.localAnswerId.set(answer.id);
              this.answerIdUpdate.emit({ cardId: this.id(), answerId: answer.id });
            }
            this.callInsightsAPI(answer);
          }
        });
      return;
    }

    console.error('No answer asset available for insights generation');
    this.insightsError.emit('No answer data available for insights generation');
  }

  private callInsightsAPI(answerAsset: any): void {
    const payload = { answer: answerAsset };

    this.subscription.add(
      this.dataCopilotApiService.getInsights(payload)
        .pipe(
          catchError(error => {
            console.error('Error generating insights:', error);
            const errorMessage = error?.message || error?.error?.message || 'Failed to generate insights. Please try again.';
            this.insightsError.emit(errorMessage);
            return of(null);
          })
        )
        .subscribe({
          next: (response: any) => {
            if (response?.data?.data?.[0]) {
              const insights = response.data.data[0].parameters.INSIGHTS;
              const reportName = this.questionText() || this.cardTitle() || 'Conversation Insights';
              this.insightsGenerated.emit({ reportName, insights });
            } else {
              console.error('Invalid insights response format:', response);
              this.insightsError.emit('Invalid response format from insights API');
            }
          }
        })
    );
  }

  onVisualTypeLoaded(type: 'TABLE' | 'CHART'): void {
    if (!this.selectedVisualType()) {
      this.visualType.set(type);
      this.visualTypeChanged.emit({ cardId: this.id(), visualType: type });
    }
  }

  get isUnsaved(): boolean {
    return this.cardType() === 'FREEFORM_CARD' && this.hasUnsavedFreeFormChanges();
  }

  get isSaved(): boolean {
    return this.cardType() === 'FREEFORM_CARD' &&
           !!(this.localAssetId() || this.assetId() || this.freeFormAssetId()) &&
           !this.hasUnsavedFreeFormChanges();
  }

  loadAnswerDataForVisualization(): void {
    if (!this.questionId() || this.cardType() !== 'ANSWER') return;

    this.isLoading.set(true);
    this.hasError.set(false);

    this.subscription.add(
      this.assetsApiService.findAssetsByRelationship([this.questionId()], ['ANSWER'])
        .pipe(
          map((response: any) => response?.data?.findAssetsByRelationship || []),
          catchError(error => {
            console.error('Error getting answer relationships:', error);
            this.isLoading.set(false);
            this.hasError.set(true);
            this.errorMessage.set('Failed to load answer data: ' + (error?.message || 'Unknown error'));
            return of([]);
          })
        )
        .subscribe({
          next: (answers) => {
            if (!answers?.length) {
              this.isLoading.set(false);
              this.hasError.set(true);
              this.errorMessage.set('No answer found for this question');
              return;
            }

            const answer = answers[0];
            if (!answer) {
              this.isLoading.set(false);
              this.hasError.set(true);
              this.errorMessage.set('Failed to load answer details');
              return;
            }
            this.answerIdUpdate.emit({ cardId: this.id(), answerId: answer.id });
            this.localAnswerId.set(answer.id);
            this.currentAnswerAsset = answer;
            this.processAnswerDataForVisualization(answer);
          },
          error: (err) => {
            console.error('Subscription error:', err);
            this.isLoading.set(false);
            this.hasError.set(true);
            this.errorMessage.set('Error in subscription: ' + (err?.message || 'Unknown error'));
          }
        })
    );
  }

  processAnswerDataForVisualization(answer: any): void {
    try {
      this.isLoading.set(false);

      if (answer.id) {
        this.localAnswerId.set(answer.id);
      }

      const parameters = answer.parameters || {};

      if (parameters.answer_format && !this.selectedVisualType()) {
        const mode = parameters.answer_format.toLowerCase() as 'grid' | 'chart';
        this.viewMode.set(mode);
        this.visualTypeChanged.emit({ cardId: this.id(), visualType: mode === 'grid' ? 'TABLE' : 'CHART' });
      }

      if (parameters.chart_configurations) {
        this.loadChartConfigurationFromAnswer(parameters.chart_configurations);
      }

      if (parameters.grid_configurations) {
        this.loadGridConfigurationFromAnswer(parameters.grid_configurations);
      }

      if (Array.isArray(parameters.DATA) && parameters.DATA.length > 0) {
        this.setupTableDataForVisualization(parameters);

        if (this.viewMode() === 'chart') {
          const hasExistingChart = this.selectedChart() && Object.keys(this.selectedChart()).length > 0;
          const hasExistingAxisConfig = this.axisAttributesAndMeasures() && Object.keys(this.axisAttributesAndMeasures()).length > 0;
          if (!hasExistingChart || !hasExistingAxisConfig) {
            this.initializeDefaultChartConfiguration();
          }
        }

        this.cardDataLoaded.emit({ cardId: this.id(), data: { answerData: this.answerData() } });
      } else if (parameters.ERROR) {
        this.hasError.set(true);
        this.errorMessage.set(parameters.ERROR);
      } else {
        this.hasError.set(true);
        this.errorMessage.set('No table data available for this question');
      }
    } catch (error) {
      this.isLoading.set(false);
      this.hasError.set(true);
      this.errorMessage.set('Failed to process answer data');
      console.error('Error processing answer:', error);
    }
  }

  setupTableDataForVisualization(parameters: any): void {
    try {
      const data = parameters.DATA || [];

      if (data.length === 0) {
        this.hasError.set(true);
        this.errorMessage.set('No data available in the answer');
        return;
      }

      const columns = Object.keys(data[0]);
      const headers = columns.map((column: string) => ({
        headerName: column,
        field: column,
        filter: 'agTextColumnFilter',
        sortable: true
      }));

      this.tableData.set({ headers, rows: data });
      this.answerData.set(data);
    } catch (error) {
      this.hasError.set(true);
      this.errorMessage.set('Failed to process table data');
      console.error('Error setting up table data:', error);
    }
  }

  onViewModeChange(newMode: 'grid' | 'chart'): void {
    this.viewMode.set(newMode);
    if (newMode === 'chart') {
      const hasExistingChart = this.selectedChart() && Object.keys(this.selectedChart()).length > 0;
      const hasExistingAxisConfig = this.axisAttributesAndMeasures() && Object.keys(this.axisAttributesAndMeasures()).length > 0;
      if (!hasExistingChart || !hasExistingAxisConfig) {
        this.initializeDefaultChartConfiguration();
      }
    }
    this.visualTypeChanged.emit({ cardId: this.id(), visualType: newMode === 'grid' ? 'TABLE' : 'CHART' });
  }

  onChartModelChange(chartModel: ChartModel): void {
    this.localChartModel.set(chartModel);
    this.chartType.set(chartModel.chartType);
    this.chartConfigurationsChanged.emit({
      cardId: this.id(),
      chartConfigurations: { chartModel, chartType: chartModel.chartType, 'ag-grid': true }
    });
  }

  onFilterChange(filterModel: any): void {
    this.localFilterModel.set(filterModel);
    this.gridConfigurationsChanged.emit({
      cardId: this.id(),
      gridConfigurations: { filterModel, sortModel: this.localSortModel() }
    });
  }

  onSortChange(sortModel: ColumnState[]): void {
    this.localSortModel.set(sortModel);
    this.gridConfigurationsChanged.emit({
      cardId: this.id(),
      gridConfigurations: { filterModel: this.localFilterModel(), sortModel }
    });
  }

  onReportChartConfigurationsChanged(chartConfigurations: any): void {
    this._latestReportChartConfig = chartConfigurations;
    this.chartConfigurationsChanged.emit({ cardId: this.id(), chartConfigurations });
  }

  onReportGridConfigurationsChanged(gridConfigurations: any): void {
    this._latestReportGridConfig = gridConfigurations;
    this.gridConfigurationsChanged.emit({ cardId: this.id(), gridConfigurations });
  }

  private initializeDefaultChartConfiguration(): void {
    const data = this.answerData();
    if (!data?.length) return;

    const hasExistingChart = this.selectedChart() && Object.keys(this.selectedChart()).length > 0;
    const hasExistingAxisConfig = this.axisAttributesAndMeasures() && Object.keys(this.axisAttributesAndMeasures()).length > 0;
    if (hasExistingChart && hasExistingAxisConfig) return;

    const firstRow = data[0];
    const keys = Object.keys(firstRow);
    const numericalFields: string[] = [];
    const categoricalFields: string[] = [];
    const timeFields: string[] = [];

    keys.forEach(key => {
      const sampleValue = firstRow[key];
      if (typeof sampleValue === 'number' || (!isNaN(Number(sampleValue)) && sampleValue !== null && sampleValue !== '')) {
        numericalFields.push(key);
      } else if (this.isTimeField(sampleValue)) {
        timeFields.push(key);
      } else {
        categoricalFields.push(key);
      }
    });

    const xAxis = timeFields.length > 0 ? timeFields[0] : (categoricalFields.length > 0 ? categoricalFields[0] : keys[0]);
    const yAxis = numericalFields.length > 0 ? numericalFields[0] : keys[1];

    if (xAxis && yAxis) {
      this.axisAttributesAndMeasures.set({ xAxis, yAxis, numericalFields, categoricalFields, timeFields });

      if (!hasExistingChart) {
        this.chartType.set('bar');
        this.selectedChart.set({
          id: 'bar-chart', type: 'bar', label: 'Bar Chart', description: 'Default bar chart',
          disabled: false, tags: { class: '' }, status: '', icon: ''
        });
      }
    }
  }

  private isTimeField(value: any): boolean {
    if (!value) return false;
    const datePattern = /^\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}|\d{2}-\d{2}-\d{4}/;
    return typeof value === 'string' && datePattern.test(value) && !isNaN(Date.parse(value));
  }

  private updateAssetTitle(newTitle: string): void {
    const assetId = this.localAssetId() || this.assetId() || this.freeFormAssetId();
    if (!assetId) return;

    const currentContent = this.freeFormEditorComp?.content() || '';

    const assetData = {
      id: assetId,
      name: newTitle,
      type: 'FREEFORM_CARD',
      status: 'OPEN',
      description: '',
      tags: [],
      parameters: { content: currentContent }
    };

    this.assetsApiService.updateAsset(assetData).subscribe({
      next: () => {},
      error: (err: Error) => {
        console.error('Error updating asset title:', err);
      }
    });
  }

  private loadChartConfigurationFromAnswer(chartConfigurations: any): void {
    if (this.chartConfigurations()?.chartModel) return;

    if (chartConfigurations) {
      if (chartConfigurations['ag-grid'] === true && chartConfigurations.chartModel) {
        this.localChartModel.set(chartConfigurations.chartModel);
        if (chartConfigurations.chartType) {
          this.chartType.set(chartConfigurations.chartType);
        }
      } else {
        if (chartConfigurations.selectedChart && Object.keys(chartConfigurations.selectedChart).length > 0) {
          this.selectedChart.set(chartConfigurations.selectedChart);
          if (chartConfigurations.selectedChart.type) {
            this.chartType.set(chartConfigurations.selectedChart.type);
          }
        }
        if (chartConfigurations.selectedChartParams && Object.keys(chartConfigurations.selectedChartParams).length > 0) {
          this.axisAttributesAndMeasures.set(chartConfigurations.selectedChartParams);
        }
      }
    }
  }

  private loadGridConfigurationFromAnswer(gridConfigurations: any): void {
    if (this.gridConfigurations()) return;

    if (gridConfigurations) {
      if (gridConfigurations.filterModel) {
        this.localFilterModel.set(gridConfigurations.filterModel);
      }
      if (gridConfigurations.sortModel?.length > 0) {
        this.localSortModel.set(gridConfigurations.sortModel);
      }
    }
  }

  private loadPreloadedAnswerData(_data: any): void {
    try {
      this.loadAnswerDataForVisualization();
    } catch (error) {
      console.error('Error loading preloaded answer data:', error);
      this.isLoading.set(false);
      this.hasError.set(true);
      this.errorMessage.set('Failed to load saved answer data');
    }
  }

  private loadPreloadedReportData(_data: any): void {
    try {
    } catch (error) {
      console.error('Error loading preloaded report data:', error);
    }
  }

  public getLatestChartConfigurations(): CardChartConfigurations | null {
    if (this.cardType() === 'REPORT') {
      if (this.reportFrameworkComp) {
        const chartModel = this.reportFrameworkComp.getCurrentChartModel();
        if (chartModel) {
          return { chartModel, chartType: chartModel.chartType, 'ag-grid': true };
        }
      }
      if (this._latestReportChartConfig) return this._latestReportChartConfig;
    } else if (this.cardType() === 'ANSWER') {
      if (this.dataVisualizationComp) {
        const chartModel = this.dataVisualizationComp.getCurrentChartModel();
        if (chartModel) {
          return { chartModel, chartType: chartModel.chartType, 'ag-grid': true };
        }
      }
      const local = this.localChartModel();
      if (local) {
        return { chartModel: local, chartType: local.chartType, 'ag-grid': true };
      }
    }
    return this.chartConfigurations() || null;
  }

  public getLatestGridConfigurations(): CardGridConfigurations | null {
    if (this.cardType() === 'REPORT') {
      if (this._latestReportGridConfig) return this._latestReportGridConfig;
    } else if (this.cardType() === 'ANSWER') {
      const filterModel = this.localFilterModel();
      const sortModel = this.localSortModel();
      if (filterModel || sortModel?.length > 0) {
        return { filterModel, sortModel };
      }
    }
    return this.gridConfigurations() || null;
  }
}
